// src/components/admin/flows/TemplateLibrary.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    BookTemplate, UserCheck, UserMinus, ClipboardCheck, ListChecks,
    Bell, Star, ArrowRight, GitBranch, DollarSign
} from 'lucide-react';
import { useFlowAutomation, generateId } from '../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../contexts/ThemeContext';

const CATEGORY_ICONS = {
    onboarding: UserCheck,
    offboarding: UserMinus,
    approval: ClipboardCheck,
    task: ListChecks,
    notification: Bell,
    payroll: DollarSign,
};

const CATEGORIES = [
    { value: '', label: 'All Templates' },
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'offboarding', label: 'Offboarding' },
    { value: 'approval', label: 'Approval' },
    { value: 'task', label: 'Task' },
    { value: 'notification', label: 'Notification' },
    { value: 'payroll', label: 'Payroll' },
];

const TemplateLibrary = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';
    const { templates, fetchTemplates, initNewWorkflow, saveWorkflow } = useFlowAutomation();

    const [category, setCategory] = useState('');
    const [creating, setCreating] = useState(null);

    useEffect(() => {
        fetchTemplates(category || undefined);
    }, [fetchTemplates, category]);

    const handleUseTemplate = async (template) => {
        setCreating(template.id);

        // Build ID mapping BEFORE creating the workflow
        const idMap = {};
        const newSteps = template.steps.map(s => {
            const newId = generateId('step');
            idMap[s.id] = newId;
            return { ...s, id: newId };
        });

        const newConnections = template.connections.map(c => ({
            ...c,
            id: generateId('conn'),
            from_step_id: idMap[c.from_step_id] || c.from_step_id,
            to_step_id: idMap[c.to_step_id] || c.to_step_id,
        }));

        // workflows.type has a DB CHECK constraint — map template categories to
        // allowed types and fold everything else (notification, payroll, …)
        // into 'custom' so instantiation never violates it.
        const TYPE_BY_CATEGORY = {
            onboarding: 'onboarding',
            offboarding: 'offboarding',
            approval: 'approval_chain',
            task: 'task_automation',
        };
        const workflow = initNewWorkflow({
            name: `${template.name} (copy)`,
            description: template.description,
            type: TYPE_BY_CATEGORY[template.category] || 'custom',
            steps: newSteps,
            connections: newConnections,
            trigger: template.trigger,
            template_id: template.id,
        });

        const result = await saveWorkflow(workflow);
        setCreating(null);

        if (result?.workflow?.id) {
            navigate(`/admin/flows/builder/${result.workflow.id}`);
        }
    };

    return (
        <div className="space-y-6">
            {/* Category Filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {CATEGORIES.map((cat) => (
                    <button
                        key={cat.value}
                        onClick={() => setCategory(cat.value)}
                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                            category === cat.value
                                ? 'bg-blue-500 text-white'
                                : isDark
                                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                    >
                        {cat.label}
                    </button>
                ))}
            </div>

            {/* Template Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {templates.map((template) => {
                    const CategoryIcon = CATEGORY_ICONS[template.category] || BookTemplate;
                    return (
                        <div
                            key={template.id}
                            className={`rounded-xl border p-5 transition-all hover:shadow-md ${
                                isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-start gap-3 mb-3">
                                <div className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                    <CategoryIcon className={`w-5 h-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {template.name}
                                    </h3>
                                    <span className={`text-xs capitalize px-2 py-0.5 rounded mt-1 inline-block ${
                                        isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {template.category}
                                    </span>
                                </div>
                            </div>

                            <p className={`text-xs mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {template.description}
                            </p>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        <GitBranch className="w-3 h-3" />
                                        {template.steps?.length || 0} steps
                                    </span>
                                    <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        <Star className="w-3 h-3" />
                                        {template.popularity}%
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleUseTemplate(template)}
                                    disabled={creating === template.id}
                                    className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 font-medium disabled:opacity-50"
                                >
                                    {creating === template.id ? 'Creating...' : 'Use'}
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {templates.length === 0 && (
                <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <BookTemplate className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No templates found for this category</p>
                </div>
            )}
        </div>
    );
};

export default TemplateLibrary;
